import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setupWholesaleTest } from '../utils/setupWholesaleTest';

const WholesaleTestSetup = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSetup = async () => {
    if (!currentUser) {
      setError('Please log in first');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await setupWholesaleTest(currentUser.uid);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Wholesale Test Setup
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create test data for wholesale dashboard
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm space-y-4">
            {/* User Info */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900">Current User</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {currentUser ? (
                    <>
                      <strong>ID:</strong> {currentUser.uid}<br />
                      <strong>Email:</strong> {currentUser.email}
                    </>
                  ) : (
                    'Not logged in'
                  )}
                </p>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleSetup}
              disabled={loading || !currentUser}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                loading || !currentUser
                  ? 'bg-purple-300 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {loading ? 'Setting up...' : 'Setup Wholesale Test Data'}
            </button>

            {/* Status Messages */}
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Success!
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Wholesale test data has been set up successfully.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900">Instructions</h3>
            <div className="mt-2 text-sm text-gray-500 space-y-2">
              <p>This will create:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Update your user document with wholesale access</li>
                <li>Create a wholesale customer document with test data</li>
                <li>Set the status to 'approved'</li>
              </ul>
              <p className="mt-4 text-xs text-gray-400">
                Note: This is for testing purposes only. The data can be safely deleted later.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleTestSetup; 